import { useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import AdminTopBar from './AdminTopBar';
import AdminSidebar from './AdminSidebar';
import { ADMIN_BACKGROUND, ADMIN_SECTIONS, PERMANENT_SIDEBAR_BREAKPOINT } from './adminSections';
import Loading from '../../../components/Loading';
import { Routes } from '../../../routes/RouteConstants';
import { StaffProvider, useStaff } from '../StaffContext';

const ADMIN_ONLY_PATHS = ADMIN_SECTIONS.filter((section) => section.adminOnly).map((section) => section.to);

/**
 * The page, once it is known who is signed in (`staff/me`). Someone who is no member of staff has
 * had the snackbar saying so, and gets no page; a provider who is no admin is sent from the
 * tenant's own pages to the calendar (docs/MULTI_PROVIDER.md, flow 3).
 */
const StaffOutlet = () => {
  const { loading, error, isAdmin } = useStaff();
  const { pathname } = useLocation();

  if (loading) return <Box sx={{ py: 5 }}><Loading /></Box>;
  if (error) {
    return (
      <Typography align="center" sx={{ py: 5, color: 'text.secondary' }}>
        Az admin felület nem érhető el ezzel a fiókkal.
      </Typography>
    );
  }
  if (!isAdmin && ADMIN_ONLY_PATHS.includes(pathname)) return <Navigate to={Routes.DASHBOARD} replace />;

  return <Outlet />;
};

/**
 * The frame of everything under /dashboard (DECISIONS.md D32, phase 2): the admin's own top bar
 * and menu in place of the guest header and footer, which RootLayout leaves off these routes.
 * It sits inside RootLayout all the same, so the tenant is loaded and the snackbar is there
 * before any admin page renders.
 */
const AdminLayout = () => {
  const theme = useTheme();
  const isPermanent = useMediaQuery(theme.breakpoints.up(PERMANENT_SIDEBAR_BREAKPOINT));
  // On a phone the bar has no room for „Vendég oldal”, so it moves to the foot of the drawer.
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [navOpen, setNavOpen] = useState(false);

  return (
    <StaffProvider>
      <Box sx={{ minHeight: '100vh', bgcolor: ADMIN_BACKGROUND, display: 'flex', flexDirection: 'column' }}>
        <AdminTopBar
          showMenuButton={!isPermanent}
          showGuestSiteButton={!isPhone}
          onMenuClick={() => setNavOpen(true)}
        />

        <Box sx={{ display: 'flex', alignItems: 'stretch', flex: 1 }}>
          <AdminSidebar
            isPermanent={isPermanent}
            showGuestSiteButton={isPhone}
            mobileOpen={navOpen}
            onMobileClose={() => setNavOpen(false)}
          />

          <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
            <StaffOutlet />
          </Box>
        </Box>
      </Box>
    </StaffProvider>
  );
};

export default AdminLayout;
